import { Component, useEffect, useRef, type ErrorInfo, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import { useLingui } from '@lingui/react';
import { AlertTriangle, RotateCw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Sentry, logger } from '@/lib/sentry';

interface BoundaryProps {
  /** A change resets the boundary: navigating away from a crashed page clears it. */
  resetKey: string;
  children: ReactNode;
}

interface BoundaryState {
  error: unknown;
  hasError: boolean;
}

/**
 * A lazy route chunk that failed to load (a tab older than the last deploy).
 * `React.lazy` caches the rejection, so only a page reload can recover.
 */
function isChunkLoadError(error: unknown): boolean {
  const text = error instanceof Error ? `${error.name} ${error.message}` : String(error);
  return /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|ChunkLoadError/i.test(
    text,
  );
}

class PageErrorBoundary extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { error: null, hasError: false };

  static getDerivedStateFromError(error: unknown): BoundaryState {
    return { error, hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    logger.error('Page render failed', {
      context: 'RouteErrorBoundary',
      path: this.props.resetKey,
      error,
    });
    Sentry.captureException(error, {
      contexts: { react: { componentStack: info.componentStack ?? undefined } },
      tags: { component: 'RouteErrorBoundary' },
    });
  }

  componentDidUpdate(prevProps: BoundaryProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null, hasError: false });
    }
  }

  reset = () => this.setState({ error: null, hasError: false });

  render() {
    if (this.state.hasError) {
      return <PageErrorFallback onRetry={this.reset} isChunkError={isChunkLoadError(this.state.error)} />;
    }
    return this.props.children;
  }
}

function reloadPage() {
  window.location.reload();
}

function PageErrorFallback({ onRetry, isChunkError }: { onRetry: () => void; isChunkError: boolean }) {
  const { i18n } = useLingui();
  const headingRef = useRef<HTMLHeadingElement>(null);

  // The page content was replaced under the user's focus; move it to the message.
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <div role="alert" className="flex justify-center px-4 py-16">
      <div className="flex w-full max-w-md flex-col items-center gap-4 rounded-lg border bg-card p-8 text-center text-card-foreground shadow-sm">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
          <AlertTriangle className="h-6 w-6 text-destructive" aria-hidden="true" />
        </span>
        <div className="space-y-2">
          {/* The page and its h1 are gone; this is the page's heading now. */}
          <h1 ref={headingRef} tabIndex={-1} className="text-lg font-semibold outline-none">
            {i18n._({ id: 'errorBoundary.page.title', message: 'This page could not be displayed' })}
          </h1>
          <p className="text-sm text-muted-foreground">
            {i18n._({
              id: 'errorBoundary.page.description',
              message: 'An unexpected error occurred. Try again, or open another page from the menu.',
            })}
          </p>
        </div>
        <div className="flex flex-wrap justify-center gap-2">
          {/* A failed chunk fails again on re-render, so "Try again" reloads too. */}
          <Button type="button" onClick={isChunkError ? reloadPage : onRetry}>
            <RotateCw aria-hidden="true" />
            {i18n._({ id: 'common.retry', message: 'Try again' })}
          </Button>
          <Button type="button" variant="outline" onClick={reloadPage}>
            {i18n._({ id: 'errorBoundary.reload', message: 'Reload page' })}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Error boundary for the page outlet. A render error replaces only the page
 * area with a localised card, so the sidebar and top bar stay usable; "Try
 * again" re-renders the page, "Reload page" loads the app again (the only
 * cure for a route chunk that failed to load), and any navigation clears the
 * error.
 */
export function RouteErrorBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  return <PageErrorBoundary resetKey={pathname}>{children}</PageErrorBoundary>;
}
